import { Component, computed, DestroyRef, effect, inject, input, OnInit, signal } from '@angular/core';
import { ILpCanvasComponent, ILpCanvasElementContext, LpImgComponent } from '@gametailors/leerplus';
import { LpRidProvinceCanvasComponent } from '../../../../typings/schema';
import { LEERPLUS } from '../../rid-game.utils';
import { RidJourneyService } from '../../services/rid-journey.service';

/** Makes the canvas element the province's area on the overview map. While the province is locked the element is hidden, so it can't be clicked or hovered. Otherwise its colored image shows on the gray map while hovered, and all the time while it's chosen and once it's explored, so the map colors in as the player works through it. A click chooses the province while it's one to choose from, and opens its map otherwise. */
@Component({
  selector: 'rid-province-canvas-component',
  imports: [LpImgComponent],
  template: `
    @if (isColored() || isHovered()) {
      <lp-img class="block! size-full" [file]="comp().coloredImage" fit="contain" />
    }
  `,
  host: {
    class: 'block size-full',
  },
})
export class RidProvinceCanvasComponent implements OnInit, ILpCanvasComponent<LpRidProvinceCanvasComponent> {
  private readonly $destroyRef = inject(DestroyRef);
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridJourneyService = inject(RidJourneyService);

  comp = input.required<LpRidProvinceCanvasComponent>();
  context = input.required<ILpCanvasElementContext>();

  protected readonly isHovered = signal(false);
  protected readonly isColored = computed(() => {
    const province = this.comp().province;
    return this.$ridJourneyService.chosenProvince()?.uid === province.uid || this.$ridJourneyService.isProvinceExplored(province);
  });

  constructor() {
    effect(() => this.context().setVisibility(!this.$ridJourneyService.isProvinceLocked(this.comp().province)));
  }

  ngOnInit(): void {
    // the same hover highlight Leerplus gives the elements it makes clickable (OnClickCanvasComponent)
    const context = this.context()
      .requirePointerEvents('pointer')
      .transition('filter 0.25s ease')
      .setHoverStyle({ filter: 'brightness(1.2)' })
      .onClick(() => this.onClick());
    // the element's mask lets the pointer pass through its transparent pixels, so hovering follows the province's shape
    const el = context.el.nativeElement;
    const onEnter = () => this.isHovered.set(true);
    const onLeave = () => this.isHovered.set(false);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    this.$destroyRef.onDestroy(() => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    });
  }

  private async onClick() {
    const province = this.comp().province;
    if (this.$ridJourneyService.chooseProvince(province)) return;

    await this.$lpApiService.switchMap(province.map, this.$lpApiService.getProjectSettings().code.defaultMapTransition);
  }
}
